import { styled } from "@pigment-css/react";
import { Repeat } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { RecurrenceCadence } from "@acto/gql/graphql";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { Switch } from "@acto/taskpane/field-sales-2/components/primitives/Switch";
import { CadenceOptions } from "./CadenceOptions";

// One slot for the switch row + cadence Collapse, so the closed (zero-height) Collapse adds no gap.
const Section = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const SwitchRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const CadencePad = styled("div")(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[24],
}));

const RepeatIcon = styled("span")(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 56,
  height: 52,
  borderRadius: theme.fieldSalesTwo.radius[16],
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
  color: theme.fieldSalesTwo.colors.neutral[600],
}));

const RepeatLabel = styled("span")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

interface RepeatFieldProps {
  repeat: boolean;
  /** Omit to lock the switch: standalone↔series conversion is not supported. */
  onRepeatChange?: (repeat: boolean) => void;
  cadence: RecurrenceCadence;
  onCadenceChange: (cadence: RecurrenceCadence) => void;
}

export function RepeatField({ repeat, onRepeatChange, cadence, onCadenceChange }: RepeatFieldProps) {
  const t = useTranslations("fieldSalesTwo.planMeeting");

  return (
    <Section>
      <SwitchRow>
        <RepeatIcon>
          <Repeat width={20} height={17} aria-hidden="true" />
        </RepeatIcon>
        <RepeatLabel>{t("repeat")}</RepeatLabel>
        <Switch
          checked={repeat}
          onCheckedChange={(next) => onRepeatChange?.(next)}
          disabled={!onRepeatChange}
          aria-label={t("repeat")}
        />
      </SwitchRow>
      <Collapse open={repeat}>
        <CadencePad>
          <CadenceOptions value={cadence} onChange={onCadenceChange} />
        </CadencePad>
      </Collapse>
    </Section>
  );
}
